import store from '../../state';
import { ClimateService } from '../../services/ClimateService';
import { getElevationAt } from '../../services/PoiDataService';

export class ClimatePanel {
  private lastPoiKey: string = '';
  private currentElevation: number = 1000;
  private minElevation: number = 200;
  private maxElevation: number = 3200;
  private container: HTMLElement | null = null;
  private sampledDailyCache: Map<string, (number | null)[]> = new Map();
  private sampledMonthlyCache: Map<string, number[]> = new Map();
  private sampledWeeklyCache: Map<string, { depth: number[]; swe: number[] }> = new Map();
  public onClose?: () => void;
  public onOpenPoiModal?: () => void;
  private onDateSelected?: (dateStr: string) => void;

  public setOnClose(cb: () => void) {
    this.onClose = cb;
  }

  public setOnOpenPoiModal(cb: () => void) {
    this.onOpenPoiModal = cb;
  }

  private getLegendHtml(selectedVar: string, isImperial: boolean = true): string {
    let gradient = 'linear-gradient(to right, #38bdf8, #22c55e, #eab308, #ef4444)';
    let minLabel = '0';
    let midLabel = '50';
    let maxLabel = '100';
    let unitLabel = '';

    if (selectedVar.startsWith('tmax') || selectedVar.startsWith('tmin')) {
      gradient = 'linear-gradient(to right, #4a148c 0%, #1565c0 15%, #4fc3f7 30%, #2e7d32 45%, #ffb300 60%, #f4511e 75%, #b71c1c 88%, #f472b6 96%, #ffffff 100%)';
      minLabel = isImperial ? '-40°F' : '-40°C';
      midLabel = isImperial ? '50°F' : '10°C';
      maxLabel = isImperial ? '125°F+' : '52°C+';
      unitLabel = 'Temperature';
    } else if (selectedVar === 'precip_prob') {
      gradient = 'linear-gradient(to right, rgba(56,189,248,0.1) 0%, rgba(56,189,248,0.5) 40%, #0284c7 70%, #1e40af 100%)';
      minLabel = '0%';
      midLabel = '40%';
      maxLabel = '80%+';
      unitLabel = 'Precip Probability';
    } else if (selectedVar === 'precip_monthly') {
      gradient = 'linear-gradient(to right, #fef08a 0%, #22c55e 30%, #0284c7 70%, #1e3a8a 100%)';
      minLabel = '0.0"';
      midLabel = '4.0"';
      maxLabel = '12.0"+';
      unitLabel = 'Monthly Precipitation';
    } else if (selectedVar === 'snow_monthly' || selectedVar === 'snowpack') {
      gradient = 'linear-gradient(to right, #0f172a 0%, #38bdf8 25%, #818cf8 60%, #c084fc 85%, #ffffff 100%)';
      minLabel = '0"';
      midLabel = selectedVar === 'snow_monthly' ? '12"' : '30"';
      maxLabel = selectedVar === 'snow_monthly' ? '36"+' : '90"+';
      unitLabel = selectedVar === 'snow_monthly' ? 'Fresh Snowfall' : 'Snowpack Depth';
    } else if (selectedVar === 'snow_swe') {
      gradient = 'linear-gradient(to right, #0f172a 0%, #38bdf8 30%, #3b82f6 70%, #9333ea 100%)';
      minLabel = '0"';
      midLabel = '10"';
      maxLabel = '30"+';
      unitLabel = 'Snow Water Eq (SWE)';
    } else if (selectedVar === 'mostly_sunny') {
      gradient = 'linear-gradient(to right, rgba(254,240,138,0.2) 0%, #fef08a 25%, #fbbf24 50%, #f97316 75%, #b45309 100%)';
      minLabel = '0%';
      midLabel = '50%';
      maxLabel = '100%';
      unitLabel = 'Mostly Sunny Likelihood';
    } else if (selectedVar === 'partly_cloudy') {
      gradient = 'linear-gradient(to right, rgba(167,243,208,0.2) 0%, #a7f3d0 25%, #38bdf8 50%, #0284c7 75%, #1e40af 100%)';
      minLabel = '0%';
      midLabel = '50%';
      maxLabel = '100%';
      unitLabel = 'Partly Cloudy Likelihood';
    } else if (selectedVar === 'overcast') {
      gradient = 'linear-gradient(to right, rgba(218,226,236,0.3) 0%, #dae2ec 25%, #94a3b8 50%, #5a6980 75%, #141c2a 100%)';
      minLabel = '0%';
      midLabel = '50%';
      maxLabel = '100%';
      unitLabel = 'Socked-in / Overcast Likelihood';
    } else if (selectedVar === 'sunshine_pct' || selectedVar === 'sunshine_pct_monthly') {
      gradient = 'linear-gradient(to right, #1e293b 0%, #475569 20%, #0284c7 45%, #fde047 60%, #f97316 75%, #e11d48 100%)';
      minLabel = '0%';
      midLabel = '50%';
      maxLabel = '100%';
      unitLabel = selectedVar === 'sunshine_pct_monthly' ? 'Mean Monthly Sunshine Exposure' : 'Mean Daily Sunshine Exposure';
    } else if (selectedVar === 'solar_energy' || selectedVar === 'solar_energy_monthly') {
      gradient = 'linear-gradient(to right, #1e1b4b 0%, #0284c7 20%, #eab308 45%, #f97316 70%, #c026d3 100%)';
      minLabel = '0 kWh/m²';
      midLabel = '5.0 kWh/m²';
      maxLabel = '10+ kWh/m²';
      unitLabel = selectedVar === 'solar_energy_monthly' ? 'Average Daily Solar Energy (Monthly)' : 'Average Daily Solar Energy';
    } else if (selectedVar === 'mostly_sunny_monthly' || selectedVar === 'partly_cloudy_monthly' || selectedVar === 'overcast_monthly') {
      gradient = selectedVar === 'mostly_sunny_monthly' 
        ? 'linear-gradient(to right, rgba(254,240,138,0.2) 0%, #fef08a 30%, #fbbf24 65%, #ea580c 100%)'
        : (selectedVar === 'partly_cloudy_monthly' 
          ? 'linear-gradient(to right, rgba(167,243,208,0.2) 0%, #67e8f9 30%, #38bdf8 65%, #1d4ed8 100%)'
          : 'linear-gradient(to right, rgba(218,226,236,0.3) 0%, #dae2ec 30%, #5a6980 65%, #141c2a 100%)');
      minLabel = '0 d';
      midLabel = '15 d';
      maxLabel = '31 d';
      unitLabel = selectedVar === 'mostly_sunny_monthly' ? 'Monthly Mostly Sunny Days' : (selectedVar === 'partly_cloudy_monthly' ? 'Monthly Partly Cloudy Days' : 'Monthly Overcast Days');
    } else if (selectedVar.startsWith('days_gt_') || selectedVar.startsWith('days_lt_') || selectedVar === 'precip_days') {
      gradient = 'linear-gradient(to right, rgba(255,255,255,0.05) 0%, #38bdf8 30%, #f59e0b 65%, #ef4444 100%)';
      minLabel = '0 d';
      midLabel = '15 d';
      maxLabel = '31 d';
      unitLabel = 'Monthly Days Count';
    } else if (selectedVar === 'wind_speed') {
      gradient = 'linear-gradient(to right, rgba(255,255,255,0.05) 0%, #166534 18%, #84cc16 36%, #eab308 54%, #ea580c 72%, #c026d3 88%, #2563eb 100%)';
      minLabel = '0 mph';
      midLabel = '14 mph';
      maxLabel = '26+ mph';
      unitLabel = 'Average Wind Speed';
    }

    return `
      <div style="background: rgba(15, 23, 42, 0.7); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 10px; display: flex; flex-direction: column; gap: 4px;">
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; font-weight: 700; color: #94a3b8; text-transform: uppercase;">
          <span>${unitLabel}</span>
          <span>Color Scale</span>
        </div>
        <div style="height: 8px; width: 100%; border-radius: 4px; background: ${gradient}; border: 1px solid rgba(255,255,255,0.15);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; font-weight: 800; color: #f8fafc;">
          <span>${minLabel}</span>
          <span>${midLabel}</span>
          <span>${maxLabel}</span>
        </div>
      </div>
    `;
  }

  public render(state: any): string {
    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];

    const defaultInfo = ClimateService.getDefaultDateInfo();
    const monthIndex = state.climateMonthIndex !== undefined ? state.climateMonthIndex : defaultInfo.monthIndex;
    const selectedDateStr = state.climateSelectedDate || defaultInfo.dateStr;
    const viewMode = state.climateViewMode || 'daily';
    const selectedVar = state.climateSelectedVariable || 'tmax_mean';
    const selectedWeek = state.climateSelectedWeek || 11; // Default to mid-March
    const isImperial = state.units === 'imperial';

    const locKey = `${lat.toFixed(3)}_${lon.toFixed(3)}`;
    if (locKey !== this.lastPoiKey) {
      this.lastPoiKey = locKey;
      if (poi && poi.elev && poi.elev > 0) {
        this.currentElevation = poi.elev;
        if (poi.minElev !== undefined) this.minElevation = poi.minElev;
        if (poi.maxElev !== undefined) this.maxElevation = poi.maxElev;
      } else {
        getElevationAt(lat, lon).then(prof => {
          if (prof && prof.elevation > 0) {
            this.currentElevation = prof.elevation;
            this.minElevation = prof.minElevation;
            this.maxElevation = prof.maxElevation;
            this.updateUi();
          }
        });
      }
    }

    // Trigger async PMTiles or binary tile sampling for 12 monthly blocks
    const sampleMonthKey = `${lat.toFixed(3)}_${lon.toFixed(3)}_${selectedVar}`;
    const sampledMonthVals = this.sampledMonthlyCache.get(sampleMonthKey);
    if (!sampledMonthVals && state.manifest && viewMode === 'monthly') {
      ClimateService.sampleAllMonths(state.manifest, selectedVar, lat, lon).then(vals => {
        const numeric = vals.map(v => v !== null ? v : 0);
        this.sampledMonthlyCache.set(sampleMonthKey, numeric);
        this.updateUi();
      });
    }

    // Trigger async PMTiles sampling for 52 weekly snowpack
    const sampleWeekKey = `${lat.toFixed(3)}_${lon.toFixed(3)}`;
    const sampledWeekVals = this.sampledWeeklyCache.get(sampleWeekKey);
    if (!sampledWeekVals && state.manifest && viewMode === 'weekly') {
      Promise.all([
        ClimateService.sampleAllWeeks(state.manifest, 'snowpack', lat, lon),
        ClimateService.sampleAllWeeks(state.manifest, 'snow_swe', lat, lon)
      ]).then(([dList, sList]) => {
        const depths = dList.map(v => v !== null ? v : 0);
        const swes = sList.map(v => v !== null ? v : 0);
        this.sampledWeeklyCache.set(sampleWeekKey, { depth: depths, swe: swes });
        this.updateUi();
      });
    }

    // Trigger async PMTiles or binary tile sampling for active month daily calendar (Ground Truth Observations)
    const sampleDailyKey = `${lat.toFixed(3)}_${lon.toFixed(3)}_${selectedVar}_${monthIndex}`;
    const sampledDailyVals = this.sampledDailyCache.get(sampleDailyKey);
    if (!sampledDailyVals && state.manifest && viewMode === 'daily') {
      ClimateService.sampleMonthDays(state.manifest, selectedVar, 2026, monthIndex, lat, lon).then(vals => {
        this.sampledDailyCache.set(sampleDailyKey, vals);
        this.updateUi();
      });
    }

    const fmtElev = (ft: number) => isImperial ? `${Math.round(ft).toLocaleString()} ft` : `${Math.round(ft / 3.28084).toLocaleString()} m`;

    const monthTabLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthHeaderDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Dynamic Middle Content based on View Mode
    let middleContentHtml = '';

    if (viewMode === 'monthly') {
      // 12 MONTH BLOCKS (4 columns x 3 rows)
      middleContentHtml = `
        <div style="display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="font-size: 0.78rem; font-weight: 800; color: #f8fafc;">12-Month Climatology Grid</div>
            <div style="font-size: 0.68rem; color: #38bdf8; font-weight: 700;">Selected: ${monthTabLabels[monthIndex]}</div>
          </div>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; width: 100%; box-sizing: border-box;">
            ${monthTabLabels.map((mLabel, idx) => {
              const isActive = idx === monthIndex;
              const rawVal = sampledMonthVals ? sampledMonthVals[idx] : 0;
              let valText = `${rawVal} d`;
              let valBg = 'rgba(15, 23, 42, 0.7)';

              if (selectedVar.startsWith('days_gt_')) {
                valText = `${rawVal} d`;
                if (rawVal >= 15) valBg = 'rgba(239, 68, 68, 0.5)';
                else if (rawVal >= 5) valBg = 'rgba(249, 115, 22, 0.4)';
                else if (rawVal > 0) valBg = 'rgba(234, 179, 8, 0.3)';
              } else if (selectedVar.startsWith('days_lt_')) {
                valText = `${rawVal} d`;
                if (rawVal >= 15) valBg = 'rgba(56, 189, 248, 0.5)';
                else if (rawVal >= 5) valBg = 'rgba(99, 102, 241, 0.4)';
                else if (rawVal > 0) valBg = 'rgba(147, 51, 234, 0.3)';
              } else if (selectedVar === 'precip_monthly') {
                valText = `${rawVal.toFixed(1)}"`;
                if (rawVal >= 4.0) valBg = 'rgba(2, 132, 199, 0.5)';
                else if (rawVal >= 1.5) valBg = 'rgba(34, 197, 94, 0.4)';
                else if (rawVal > 0) valBg = 'rgba(234, 179, 8, 0.25)';
              } else if (selectedVar === 'snow_monthly') {
                valText = `${rawVal.toFixed(1)}"`;
                if (rawVal >= 10.0) valBg = 'rgba(192, 132, 252, 0.5)';
                else if (rawVal > 0) valBg = 'rgba(56, 189, 248, 0.35)';
              } else if (selectedVar === 'precip_days') {
                valText = `${rawVal} d`;
                if (rawVal >= 12) valBg = 'rgba(2, 132, 199, 0.45)';
                else if (rawVal > 0) valBg = 'rgba(56, 189, 248, 0.3)';
              } else if (selectedVar === 'mostly_sunny_monthly') {
                valText = `${rawVal} d`;
                if (rawVal >= 20) valBg = 'rgba(234, 88, 12, 0.55)';
                else if (rawVal >= 12) valBg = 'rgba(245, 158, 11, 0.45)';
                else if (rawVal >= 5) valBg = 'rgba(251, 191, 36, 0.35)';
                else if (rawVal > 0) valBg = 'rgba(254, 240, 138, 0.25)';
              } else if (selectedVar === 'partly_cloudy_monthly') {
                valText = `${rawVal} d`;
                if (rawVal >= 15) valBg = 'rgba(2, 132, 199, 0.55)';
                else if (rawVal >= 8) valBg = 'rgba(56, 189, 248, 0.4)';
                else if (rawVal > 0) valBg = 'rgba(167, 243, 208, 0.25)';
              } else if (selectedVar === 'overcast_monthly') {
                valText = `${rawVal} d`;
                if (rawVal >= 15) valBg = 'rgba(30, 41, 59, 0.75)';
                else if (rawVal >= 8) valBg = 'rgba(71, 85, 105, 0.55)';
                else if (rawVal > 0) valBg = 'rgba(148, 163, 184, 0.35)';
              } else if (selectedVar === 'sunshine_pct_monthly' || selectedVar === 'sunshine_pct') {
                valText = `${Math.round(rawVal)}%`;
                if (rawVal >= 75) valBg = 'rgba(234, 88, 12, 0.55)';
                else if (rawVal >= 50) valBg = 'rgba(251, 191, 36, 0.45)';
                else if (rawVal >= 25) valBg = 'rgba(56, 189, 248, 0.35)';
                else if (rawVal > 0) valBg = 'rgba(30, 41, 59, 0.55)';
              } else if (selectedVar === 'solar_energy_monthly' || selectedVar === 'solar_energy') {
                valText = `${rawVal.toFixed(1)} kWh`;
                if (rawVal >= 7.0) valBg = 'rgba(192, 38, 211, 0.55)';
                else if (rawVal >= 5.0) valBg = 'rgba(234, 88, 12, 0.50)';
                else if (rawVal >= 3.0) valBg = 'rgba(245, 158, 11, 0.40)';
                else if (rawVal >= 1.5) valBg = 'rgba(2, 132, 199, 0.35)';
                else if (rawVal > 0) valBg = 'rgba(30, 27, 75, 0.55)';
              } else if (selectedVar === 'wind_speed') {
                valText = `${Math.round(rawVal)} mph`;
                if (rawVal >= 18) valBg = 'rgba(239, 68, 68, 0.45)';
                else if (rawVal >= 12) valBg = 'rgba(249, 115, 22, 0.35)';
              }

              const border = isActive ? '2px solid #38bdf8' : '1px solid rgba(255,255,255,0.1)';
              const shadow = isActive ? '0 0 10px rgba(56,189,248,0.5)' : 'none';

              return `
                <div 
                  class="gw-climate-month-block" 
                  data-month-idx="${idx}"
                  style="background: ${valBg}; border: ${border}; box-shadow: ${shadow}; border-radius: 6px; padding: 6px 4px; display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; transition: transform 0.15s ease, border 0.15s ease; user-select: none;">
                  <div style="font-size: 0.70rem; font-weight: 700; color: ${isActive ? '#38bdf8' : '#94a3b8'}; text-transform: uppercase;">
                    ${mLabel}
                  </div>
                  <div style="font-size: 0.85rem; font-weight: 800; color: #ffffff; margin-top: 2px;">
                    ${valText}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    } else if (viewMode === 'weekly') {
      // 52 WEEKS SNOWPACK TIMELINE & SLIDER
      const hasWeekly = sampledWeekVals !== undefined;
      const curDepth = hasWeekly ? (sampledWeekVals!.depth[selectedWeek - 1] ?? 0) : '—';
      const curSwe = hasWeekly ? (sampledWeekVals!.swe[selectedWeek - 1] ?? 0) : '—';

      // Compute exact calendar date range for the selected week
      const dStart = new Date(Date.UTC(2023, 0, 1 + (selectedWeek - 1) * 7));
      const dEnd = new Date(Date.UTC(2023, 0, Math.min(365, selectedWeek * 7)));
      const mStart = dStart.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
      const dayStart = dStart.getUTCDate();
      const mEnd = dEnd.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
      const dayEnd = dEnd.getUTCDate();
      const weekDateRange = mStart === mEnd ? `${mStart} ${dayStart}–${dayEnd}` : `${mStart} ${dayStart} – ${mEnd} ${dayEnd}`;

      middleContentHtml = `
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="font-size: 0.78rem; font-weight: 800; color: #f8fafc;">52-Week Snowpack Profile</div>
            <div style="font-size: 0.72rem; color: #38bdf8; font-weight: 800; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.3); padding: 2px 8px; border-radius: 4px;">
              Week ${selectedWeek} · ${weekDateRange}
            </div>
          </div>

          <!-- Active Week Snowpack Metric Callout -->
          <div style="background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(56, 189, 248, 0.3); border-radius: 6px; padding: 10px; display: flex; justify-content: space-around; align-items: center; box-shadow: 0 0 12px rgba(56, 189, 248, 0.15);">
            <div style="text-align: center;">
              <div style="font-size: 0.65rem; color: #94a3b8; text-transform: uppercase; font-weight: 700;">Snowpack Depth</div>
              <div style="font-size: 1.25rem; font-weight: 900; color: #38bdf8;">${typeof curDepth === 'number' ? `${curDepth}"` : '—'}</div>
            </div>
            <div style="height: 30px; width: 1px; background: rgba(255,255,255,0.1);"></div>
            <div style="text-align: center;">
              <div style="font-size: 0.65rem; color: #94a3b8; text-transform: uppercase; font-weight: 700;">Snow Water Eq (SWE)</div>
              <div style="font-size: 1.25rem; font-weight: 900; color: #c084fc;">${typeof curSwe === 'number' ? `${curSwe}"` : '—'}</div>
            </div>
          </div>

          <!-- Week Slider Controls -->
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div style="display: flex; justify-content: space-between; font-size: 0.68rem; font-weight: 700; color: #94a3b8;">
              <span style="color: #38bdf8;">Jan 1</span>
              <span style="color: #f8fafc; font-weight: 800;">Week ${selectedWeek} (${weekDateRange})</span>
              <span style="color: #38bdf8;">Dec 31</span>
            </div>
            <input 
              type="range" 
              class="gw-climate-week-slider" 
              min="1" 
              max="52" 
              value="${selectedWeek}" 
              style="width: 100%; cursor: pointer; accent-color: #38bdf8;"
            />
          </div>

          <!-- 4 Seasonal Summary Chips -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 0.72rem;">
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px;">
              <span style="color: #38bdf8; font-weight: 700;">❄️ Peak Winter:</span> <span style="font-weight: 800; color: #fff;">${sampledWeekVals ? `${Math.max(...sampledWeekVals.depth)}"` : '—'}</span>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px;">
              <span style="color: #22c55e; font-weight: 700;">🌱 Spring Melt:</span> <span style="font-weight: 800; color: #fff;">${sampledWeekVals ? `${sampledWeekVals.depth[20]}"` : '—'}</span>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px;">
              <span style="color: #eab308; font-weight: 700;">☀️ Mid Summer:</span> <span style="font-weight: 800; color: #fff;">${sampledWeekVals ? `${sampledWeekVals.depth[30]}"` : '—'}</span>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px;">
              <span style="color: #c084fc; font-weight: 700;">🍂 Peak SWE:</span> <span style="font-weight: 800; color: #fff;">${sampledWeekVals ? `${Math.max(...sampledWeekVals.swe)}"` : '—'}</span>
            </div>
          </div>
        </div>
      `;
    } else {
      // DAILY (365) CALENDAR VIEW
      const { days, firstDayCol, monthName } = ClimateService.getMonthCalendar(
        monthIndex,
        lat,
        lon,
        this.currentElevation,
        selectedDateStr
      );

      // Month navigation tabs HTML: 2 rows of 6 tabs
      const tabsHtml = monthTabLabels.map((tabLabel, idx) => {
        const isActive = idx === monthIndex;
        return `
          <button 
            class="gw-climate-month-tab ${isActive ? 'active' : ''}" 
            data-month-idx="${idx}"
            style="padding: 6px 2px; font-size: 0.72rem; font-weight: 700; border-radius: 5px; border: 1px solid ${isActive ? 'var(--color-primary, #38bdf8)' : 'rgba(255,255,255,0.1)'}; background: ${isActive ? 'rgba(56, 189, 248, 0.3)' : 'rgba(15, 23, 42, 0.6)'}; color: ${isActive ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease;">
            ${tabLabel}
          </button>
        `;
      }).join('');

      // Calendar grid cells HTML
      let calendarGridHtml = `
        <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; font-size: 0.68rem; text-align: center; font-weight: 700; color: var(--text-muted); margin-bottom: 4px; width: 100%; box-sizing: border-box;">
          ${monthHeaderDays.map(d => `<div style="min-width: 0; box-sizing: border-box; text-align: center;">${d}</div>`).join('')}
        </div>
        <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; width: 100%; box-sizing: border-box;">
      `;

      // Blank padding cells
      for (let i = 0; i < firstDayCol; i++) {
        calendarGridHtml += `<div style="height: 44px; min-width: 0; background: rgba(255,255,255,0.02); border-radius: 4px; box-sizing: border-box;"></div>`;
      }

      // Day cells
      days.forEach(day => {
        const isSelected = day.dateStr === selectedDateStr;
        const activeBorder = isSelected ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.1)';
        const activeShadow = isSelected ? '0 0 10px rgba(56,189,248,0.6)' : 'none';
        const todayIndicator = day.isToday ? `<span style="position: absolute; top: 1px; right: 2px; font-size: 0.50rem; color: #ffeb3b;">●</span>` : '';

        // Readout value directly from sampled daily PMTiles data (ground truth observations)
        let sampledVal: number | null = null;
        if (sampledDailyVals && sampledDailyVals[day.dayNum - 1] !== undefined) {
          sampledVal = sampledDailyVals[day.dayNum - 1];
        }

        let valLabel = '—';
        if (sampledVal !== null && sampledVal !== undefined) {
          if (selectedVar === 'precip_prob' || selectedVar === 'mostly_sunny' || selectedVar === 'partly_cloudy' || selectedVar === 'overcast' || selectedVar === 'sunshine_pct') {
            valLabel = `${Math.round(sampledVal)}%`;
          } else if (selectedVar === 'solar_energy') {
            valLabel = `${sampledVal.toFixed(1)}`;
          } else {
            valLabel = `${Math.round(sampledVal)}°`;
          }
        } else {
          valLabel = '—';
        }

        // Color coding matching ground truth sampled value
        let bg = 'rgba(15, 23, 42, 0.7)';
        const effectiveVal = sampledVal !== null && sampledVal !== undefined ? sampledVal : day.tmaxMean;
        if (selectedVar.startsWith('tmax') || selectedVar.startsWith('tmin')) {
          if (effectiveVal >= 105) bg = 'rgba(244, 114, 182, 0.45)';
          else if (effectiveVal >= 90) bg = 'rgba(239, 68, 68, 0.4)';
          else if (effectiveVal >= 75) bg = 'rgba(249, 115, 22, 0.35)';
          else if (effectiveVal >= 55) bg = 'rgba(34, 197, 94, 0.25)';
          else if (effectiveVal >= 32) bg = 'rgba(56, 189, 248, 0.3)';
          else if (effectiveVal >= 0) bg = 'rgba(59, 130, 246, 0.35)';
          else bg = 'rgba(126, 34, 206, 0.4)';
        } else if (selectedVar === 'precip_prob') {
          if (effectiveVal >= 75) bg = 'rgba(192, 38, 211, 0.45)';
          else if (effectiveVal >= 45) bg = 'rgba(59, 130, 246, 0.4)';
          else if (effectiveVal >= 20) bg = 'rgba(6, 182, 212, 0.3)';
          else if (effectiveVal >= 5) bg = 'rgba(167, 243, 208, 0.2)';
        } else if (selectedVar === 'mostly_sunny') {
          if (effectiveVal >= 75) bg = 'rgba(234, 88, 12, 0.55)';
          else if (effectiveVal >= 50) bg = 'rgba(245, 158, 11, 0.45)';
          else if (effectiveVal >= 25) bg = 'rgba(251, 191, 36, 0.35)';
          else if (effectiveVal >= 5) bg = 'rgba(254, 240, 138, 0.25)';
        } else if (selectedVar === 'partly_cloudy') {
          if (effectiveVal >= 75) bg = 'rgba(2, 132, 199, 0.55)';
          else if (effectiveVal >= 50) bg = 'rgba(14, 165, 233, 0.45)';
          else if (effectiveVal >= 25) bg = 'rgba(56, 189, 248, 0.35)';
          else if (effectiveVal >= 5) bg = 'rgba(167, 243, 208, 0.25)';
        } else if (selectedVar === 'overcast') {
          if (effectiveVal >= 75) bg = 'rgba(30, 41, 59, 0.75)';
          else if (effectiveVal >= 50) bg = 'rgba(51, 65, 85, 0.6)';
          else if (effectiveVal >= 25) bg = 'rgba(100, 116, 139, 0.45)';
          else if (effectiveVal >= 5) bg = 'rgba(148, 163, 184, 0.3)';
        } else if (selectedVar === 'sunshine_pct') {
          if (effectiveVal >= 75) bg = 'rgba(234, 88, 12, 0.55)';
          else if (effectiveVal >= 50) bg = 'rgba(251, 191, 36, 0.45)';
          else if (effectiveVal >= 25) bg = 'rgba(56, 189, 248, 0.35)';
          else if (effectiveVal >= 5) bg = 'rgba(30, 41, 59, 0.55)';
        } else if (selectedVar === 'solar_energy') {
          if (effectiveVal >= 7.0) bg = 'rgba(192, 38, 211, 0.55)';
          else if (effectiveVal >= 5.0) bg = 'rgba(234, 88, 12, 0.50)';
          else if (effectiveVal >= 3.0) bg = 'rgba(245, 158, 11, 0.40)';
          else if (effectiveVal >= 1.5) bg = 'rgba(2, 132, 199, 0.35)';
          else if (effectiveVal >= 0.2) bg = 'rgba(30, 27, 75, 0.55)';
        }

        calendarGridHtml += `
          <div 
            class="gw-climate-day-cell" 
            data-date="${day.dateStr}"
            title="${day.dateStr}: ${valLabel} (Observed 30-Yr Norm)"
            style="position: relative; height: 44px; min-width: 0; background: ${bg}; border-radius: 5px; border: ${activeBorder}; box-shadow: ${activeShadow}; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; padding: 2px; box-sizing: border-box; transition: transform 0.15s ease; user-select: none;">
            ${todayIndicator}
            <div style="font-size: 0.65rem; font-weight: 700; color: rgba(255,255,255,0.7); line-height: 1;">
              ${day.dayNum}
            </div>
            <div style="font-size: 0.70rem; font-weight: 800; text-align: center; color: #ffffff; text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
              ${valLabel}
            </div>
          </div>
        `;
      });

      calendarGridHtml += `</div>`;

      middleContentHtml = `
        <!-- Month Navigation Grid (6 columns x 2 rows) -->
        <div style="display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px; width: 100%; box-sizing: border-box;">
          ${tabsHtml}
        </div>

        <!-- Calendar Container -->
        <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; padding: 8px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
            <div style="font-size: 0.80rem; font-weight: 800; color: #f8fafc;">${monthName}</div>
            <div style="font-size: 0.68rem; color: var(--text-muted);">Selected: ${selectedDateStr}</div>
          </div>
          ${calendarGridHtml}
        </div>
      `;
    }

    const legendHtml = this.getLegendHtml(selectedVar, isImperial);

    return `
      <div class="gw-climate-panel" style="display: flex; flex-direction: column; gap: 10px; width: 100%; color: #fff; font-family: inherit; box-sizing: border-box;">
        
        <!-- Header -->
        <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 6px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 1.2rem;">📊</span>
            <div>
              <div style="font-weight: 800; font-size: 0.92rem; letter-spacing: 0.5px; color: #f8fafc;">30-YEAR CLIMATOLOGY</div>
              <div style="font-size: 0.68rem; color: var(--text-muted);">Trip Planning & 1991–2020 Climate Normals</div>
            </div>
          </div>
          <button class="gw-climate-close-btn" style="background: rgba(255,255,255,0.08); border: none; border-radius: 50%; width: 26px; height: 26px; color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 0.8rem;">✕</button>
        </div>

        <!-- View Mode Switcher -->
        <div style="display: flex; gap: 4px; background: rgba(0,0,0,0.3); padding: 3px; border-radius: 6px;">
          <button class="gw-climate-mode-btn ${viewMode === 'daily' ? 'active' : ''}" data-mode="daily" style="flex: 1; padding: 5px; font-size: 0.72rem; font-weight: 700; border-radius: 4px; border: none; background: ${viewMode === 'daily' ? 'rgba(56, 189, 248, 0.3)' : 'transparent'}; color: ${viewMode === 'daily' ? '#fff' : 'var(--text-muted)'}; cursor: pointer;">🗓️ Daily (365)</button>
          <button class="gw-climate-mode-btn ${viewMode === 'monthly' ? 'active' : ''}" data-mode="monthly" style="flex: 1; padding: 5px; font-size: 0.72rem; font-weight: 700; border-radius: 4px; border: none; background: ${viewMode === 'monthly' ? 'rgba(56, 189, 248, 0.3)' : 'transparent'}; color: ${viewMode === 'monthly' ? '#fff' : 'var(--text-muted)'}; cursor: pointer;">📅 Monthly (12)</button>
          <button class="gw-climate-mode-btn ${viewMode === 'weekly' ? 'active' : ''}" data-mode="weekly" style="flex: 1; padding: 5px; font-size: 0.72rem; font-weight: 700; border-radius: 4px; border: none; background: ${viewMode === 'weekly' ? 'rgba(56, 189, 248, 0.3)' : 'transparent'}; color: ${viewMode === 'weekly' ? '#fff' : 'var(--text-muted)'}; cursor: pointer;">❄️ Snowpack (52)</button>
        </div>

        <!-- Variable Selector Categorized Dropdown -->
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <label style="font-size: 0.68rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase;">Climate Layer Variable</label>
          <select class="gw-climate-var-select" style="background: rgba(15, 23, 42, 0.9); color: #fff; border: 1px solid rgba(255,255,255,0.18); border-radius: 6px; padding: 6px 8px; font-size: 0.76rem; font-weight: 600; outline: none; cursor: pointer;">
            ${viewMode === 'daily' ? `
              <optgroup label="☀️ Sunshine & Solar Energy">
                <option value="solar_energy" ${selectedVar === 'solar_energy' ? 'selected' : ''}>Average Daily Solar Energy (kWh/m²)</option>
                <option value="sunshine_pct" ${selectedVar === 'sunshine_pct' ? 'selected' : ''}>Average Sunshine Exposure (%)</option>
                <option value="mostly_sunny" ${selectedVar === 'mostly_sunny' ? 'selected' : ''}>Mostly Sunny Days (%)</option>
                <option value="partly_cloudy" ${selectedVar === 'partly_cloudy' ? 'selected' : ''}>Partly Cloudy Days (%)</option>
                <option value="overcast" ${selectedVar === 'overcast' ? 'selected' : ''}>Socked-in / Overcast Days (%)</option>
              </optgroup>
              <optgroup label="🌡️ Daily High Temperatures">
                <option value="tmax_mean" ${selectedVar === 'tmax_mean' ? 'selected' : ''}>Average High Temp (°F)</option>
                <option value="tmax_p90" ${selectedVar === 'tmax_p90' ? 'selected' : ''}>90% High Temp (Warmest 10%)</option>
                <option value="tmax_rec" ${selectedVar === 'tmax_rec' ? 'selected' : ''}>Daily Max (1991–2020)</option>
              </optgroup>
              <optgroup label="❄️ Daily Low Temperatures">
                <option value="tmin_mean" ${selectedVar === 'tmin_mean' ? 'selected' : ''}>Average Low Temp (°F)</option>
                <option value="tmin_p90" ${selectedVar === 'tmin_p90' ? 'selected' : ''}>90% Low Temp (Coldest 10%)</option>
                <option value="tmin_rec" ${selectedVar === 'tmin_rec' ? 'selected' : ''}>Daily Min (1991–2020)</option>
              </optgroup>
              <optgroup label="🌧️ Precipitation Likelihood">
                <option value="precip_prob" ${selectedVar === 'precip_prob' ? 'selected' : ''}>% Chance Measurable Precip (≥0.01")</option>
              </optgroup>
            ` : (viewMode === 'monthly' ? `
              <optgroup label="☀️ Sunshine & Solar Energy">
                <option value="solar_energy_monthly" ${selectedVar === 'solar_energy_monthly' ? 'selected' : ''}>Average Daily Solar Energy (kWh/m²)</option>
                <option value="sunshine_pct_monthly" ${selectedVar === 'sunshine_pct_monthly' ? 'selected' : ''}>Average Sunshine Exposure (%)</option>
                <option value="mostly_sunny_monthly" ${selectedVar === 'mostly_sunny_monthly' ? 'selected' : ''}>Monthly Days Mostly Sunny</option>
                <option value="partly_cloudy_monthly" ${selectedVar === 'partly_cloudy_monthly' ? 'selected' : ''}>Monthly Days Partly Cloudy</option>
                <option value="overcast_monthly" ${selectedVar === 'overcast_monthly' ? 'selected' : ''}>Monthly Days Overcast</option>
              </optgroup>
              <optgroup label="🔥 Extreme Heat Days (Monthly)">
                <option value="days_gt_90" ${selectedVar === 'days_gt_90' ? 'selected' : ''}>Monthly Days > 90°F</option>
                <option value="days_gt_95" ${selectedVar === 'days_gt_95' ? 'selected' : ''}>Monthly Days > 95°F</option>
                <option value="days_gt_100" ${selectedVar === 'days_gt_100' ? 'selected' : ''}>Monthly Days > 100°F</option>
                <option value="days_gt_105" ${selectedVar === 'days_gt_105' ? 'selected' : ''}>Monthly Days > 105°F</option>
                <option value="days_gt_110" ${selectedVar === 'days_gt_110' ? 'selected' : ''}>Monthly Days > 110°F</option>
              </optgroup>
              <optgroup label="🥶 Extreme Cold Days (Monthly)">
                <option value="days_lt_40" ${selectedVar === 'days_lt_40' ? 'selected' : ''}>Monthly Days < 40°F</option>
                <option value="days_lt_30" ${selectedVar === 'days_lt_30' ? 'selected' : ''}>Monthly Days < 30°F</option>
                <option value="days_lt_20" ${selectedVar === 'days_lt_20' ? 'selected' : ''}>Monthly Days < 20°F</option>
                <option value="days_lt_10" ${selectedVar === 'days_lt_10' ? 'selected' : ''}>Monthly Days < 10°F</option>
                <option value="days_lt_0" ${selectedVar === 'days_lt_0' ? 'selected' : ''}>Monthly Days < 0°F</option>
              </optgroup>
              <optgroup label="🌧️ Precipitation & Snowfall">
                <option value="precip_monthly" ${selectedVar === 'precip_monthly' ? 'selected' : ''}>Monthly Precipitation Average (in)</option>
                <option value="snow_monthly" ${selectedVar === 'snow_monthly' ? 'selected' : ''}>Monthly Fresh Snowfall (in)</option>
                <option value="precip_days" ${selectedVar === 'precip_days' ? 'selected' : ''}>Monthly # of Precip Days</option>
              </optgroup>
              <optgroup label="💨 Wind Speed">
                <option value="wind_speed" ${selectedVar === 'wind_speed' ? 'selected' : ''}>Average Wind Speed (mph)</option>
              </optgroup>
            ` : `
              <optgroup label="❄️ Snowpack Dynamics">
                <option value="snowpack" ${selectedVar === 'snowpack' ? 'selected' : ''}>Weekly Snowpack Depth (in)</option>
                <option value="snow_swe" ${selectedVar === 'snow_swe' ? 'selected' : ''}>Weekly Snow Water Equivalent (SWE in)</option>
              </optgroup>
            `)}
          </select>
        </div>

        <!-- Color Legend Bar -->
        ${legendHtml}

        <!-- Dynamic Middle Content (Calendar vs 12-Month Grid vs 52-Week Snowpack) -->
        ${middleContentHtml}

        <!-- Elevation Profile Quick Card -->
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px; display: flex; align-items: center; justify-content: space-between;">
          <div style="font-size: 0.72rem; color: var(--text-muted);">
            <div>📍 Elevation: <span style="color: #fff; font-weight: 700;">${fmtElev(this.currentElevation)}</span></div>
            <div style="font-size: 0.62rem; color: #94a3b8;">Local Cell: ${fmtElev(this.minElevation)} – ${fmtElev(this.maxElevation)}</div>
          </div>
          <button class="gw-climate-open-modal-btn" style="background: rgba(56, 189, 248, 0.2); border: 1px solid rgba(56, 189, 248, 0.4); border-radius: 5px; color: #38bdf8; padding: 5px 10px; font-size: 0.72rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease;">
            📈 Full Summary
          </button>
        </div>

        <!-- Baseline Disclaimer Note -->
        <div style="font-size: 0.61rem; color: #64748b; line-height: 1.35; padding: 2px 4px; border-top: 1px solid rgba(255,255,255,0.06); text-align: center;">
          ℹ️ Gridded 1km observations (1991–2020 baseline). Daily Max/Min represent extreme observed values within 1991–2020 and do not include century-long station records.
        </div>

      </div>
    `;
  }

  public bindEvents(container: HTMLElement, onDateSelected?: (dateStr: string) => void) {
    this.container = container;
    if (onDateSelected) {
      this.onDateSelected = onDateSelected;
    }
    const dateCb = this.onDateSelected;

    // Close button
    const closeBtn = container.querySelector('.gw-climate-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.onClose) this.onClose();
      });
    }

    // Month Tabs (Daily view)
    const monthTabs = container.querySelectorAll('.gw-climate-month-tab');
    monthTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const mIdx = parseInt(tab.getAttribute('data-month-idx') || '0', 10);
        const state = store.getState();
        const curDate = state.climateSelectedDate || ClimateService.getDefaultDateInfo().dateStr;
        const dayPart = parseInt(curDate.split('-')[2] || '15', 10);
        const maxDays = new Date(2026, mIdx + 1, 0).getDate();
        const safeDay = Math.max(1, Math.min(dayPart, maxDays));
        const newDateStr = `2026-${String(mIdx + 1).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`;
        store.update({ climateMonthIndex: mIdx, climateSelectedDate: newDateStr });
        if (dateCb) dateCb(newDateStr);
        this.updateUi();
      });
    });

    // Month Blocks (Monthly view)
    const monthBlocks = container.querySelectorAll('.gw-climate-month-block');
    monthBlocks.forEach(block => {
      block.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const mIdx = parseInt(block.getAttribute('data-month-idx') || '0', 10);
        store.update({ climateMonthIndex: mIdx });
        this.updateUi();
      });
    });

    // View Mode Buttons
    const modeBtns = container.querySelectorAll('.gw-climate-mode-btn');
    modeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const mode = btn.getAttribute('data-mode') as 'daily' | 'monthly' | 'weekly';
        let defaultVar = 'tmax_mean';
        if (mode === 'monthly') defaultVar = 'days_gt_90';
        else if (mode === 'weekly') defaultVar = 'snowpack';
        store.update({ climateViewMode: mode, climateSelectedVariable: defaultVar });
        this.updateUi();
      });
    });

    // Variable Selector
    const varSelect = container.querySelector('.gw-climate-var-select') as HTMLSelectElement;
    if (varSelect) {
      varSelect.addEventListener('change', () => {
        const selVal = varSelect.value;
        store.update({ climateSelectedVariable: selVal });
        this.updateUi();
      });
    }

    // Snowpack Week Slider
    const weekSlider = container.querySelector('.gw-climate-week-slider') as HTMLInputElement;
    if (weekSlider) {
      weekSlider.addEventListener('input', () => {
        const wVal = parseInt(weekSlider.value, 10);
        store.update({ climateSelectedWeek: wVal });
        this.updateUi();
      });
    }

    // Day Cells
    const dayCells = container.querySelectorAll('.gw-climate-day-cell');
    dayCells.forEach(cell => {
      cell.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const dateStr = cell.getAttribute('data-date');
        if (dateStr) {
          const parts = dateStr.split('-');
          const mIdx = parts.length > 1 ? (parseInt(parts[1], 10) - 1) : 0;
          store.update({ climateSelectedDate: dateStr, climateMonthIndex: mIdx });
          if (dateCb) dateCb(dateStr);
          this.updateUi();
        }
      });
    });

    // Full Summary Modal Button
    const modalBtn = container.querySelector('.gw-climate-open-modal-btn');
    if (modalBtn) {
      modalBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.onOpenPoiModal) {
          this.onOpenPoiModal();
        }
      });
    }
  }

  public updateUi() {
    let target: HTMLElement | null = null;
    if (this.container && document.body.contains(this.container)) {
      if (this.container.classList.contains('gw-inplace-card-body') || this.container.classList.contains('legend-card-body')) {
        target = this.container;
      } else {
        target = (this.container.querySelector('.gw-inplace-card-body')
          || this.container.querySelector('.legend-card-body')
          || this.container.querySelector('.gw-climate-panel')?.parentElement) as HTMLElement | null;
      }
    }
    if (!target) {
      target = (document.querySelector('.gw-category-layer-row[data-layer="climate_data"] .gw-inplace-card-body')
        || document.querySelector('.gw-category-layer-row[data-layer="climate"] .gw-inplace-card-body')
        || document.querySelector('.legend-card[data-layer="climate"] .legend-card-body')
        || document.querySelector('.gw-climate-panel')?.parentElement) as HTMLElement | null;
    }
    if (!target) return;
    this.container = target;
    const state = store.getState();
    target.innerHTML = this.render(state);
    this.bindEvents(target);
  }
}

